import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, Settings2, Search, Eye, Edit3, RotateCcw,
  Check, X, Loader2, ChevronRight, AlertCircle,
  Send, FileText, Clock, CheckCircle2, XCircle,
  Code, Palette, Save, TestTube, Copy, RefreshCw,
  ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

// Category icons and colors
const CATEGORY_CONFIG = {
  auth: { icon: '🔐', label: 'Autenticación', color: 'bg-blue-500/20 text-blue-400' },
  billing: { icon: '💳', label: 'Facturación', color: 'bg-green-500/20 text-green-400' },
  activity: { icon: '📊', label: 'Actividad', color: 'bg-purple-500/20 text-purple-400' },
  support: { icon: '🎫', label: 'Soporte', color: 'bg-amber-500/20 text-amber-400' },
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AdminEmailTemplates = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('templates');
  const [loading, setLoading] = useState(false);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateContent, setTemplateContent] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Editor
  const [showEditor, setShowEditor] = useState(false);
  const [editMode, setEditMode] = useState('visual'); // visual | code
  const [editorSubject, setEditorSubject] = useState('');
  const [editorHtml, setEditorHtml] = useState('');
  const [editorText, setEditorText] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  
  // Config
  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({
    postmark_api_key: '',
    sender_email: 'soporte@fascinai.pro',
    sender_name: 'FASCINAI',
    is_enabled: false
  });
  
  // Logs
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsCounts, setLogsCounts] = useState({});
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Test email
  const [testEmail, setTestEmail] = useState('');
  const [testTemplateType, setTestTemplateType] = useState('welcome');
  const [sendingTest, setSendingTest] = useState(false);
  
  // ==================== DATA FETCHING ====================
  
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/email/templates');
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Fetch templates error:', err);
    }
  }, [api]);
  
  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/email/config');
      setConfig(res.data);
      setConfigForm({
        postmark_api_key: '',
        sender_email: res.data.sender_email || 'soporte@fascinai.pro',
        sender_name: res.data.sender_name || 'FASCINAI',
        is_enabled: res.data.is_enabled || false
      });
    } catch (err) {
      console.error('Fetch config error:', err);
    }
  }, [api]);
  
  const fetchLogs = useCallback(async () => {
    try {
      const res = await api.get(`/email/logs?page=${logsPage}&limit=50`);
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
      setLogsCounts(res.data.counts || {});
    } catch (err) {
      console.error('Fetch logs error:', err);
    }
  }, [api, logsPage]);
  
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/email/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  }, [api]);
  
  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'templates') {
      fetchTemplates();
    } else if (activeTab === 'config') {
      fetchConfig();
    } else if (activeTab === 'logs') {
      fetchLogs();
      fetchStats();
    }
  }, [activeTab, fetchTemplates, fetchConfig, fetchLogs, fetchStats]);
  
  // ==================== ACTIONS ====================
  
  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    setLoading(true);
    try {
      const res = await api.get(`/email/templates/${template.type}`);
      setTemplateContent(res.data);
      setEditorSubject(res.data.subject || '');
      setEditorHtml(res.data.html_content || '');
      setEditorText(res.data.text_content || '');
    } catch (err) {
      toast.error('Error cargando template');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenEditor = () => {
    if (!templateContent) return;
    setEditorSubject(templateContent.subject || '');
    setEditorHtml(templateContent.html_content || '');
    setEditorText(templateContent.text_content || '');
    setShowEditor(true);
  };
  
  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      await api.put(`/email/templates/${selectedTemplate.type}`, {
        subject: editorSubject,
        html_content: editorHtml,
        text_content: editorText,
        is_active: true
      });
      toast.success('Template guardado');
      setShowEditor(false);
      // Reload template
      await handleSelectTemplate(selectedTemplate);
      await fetchTemplates();
    } catch (err) {
      toast.error('Error guardando template');
    } finally {
      setSaving(false);
    }
  };
  
  const handleResetTemplate = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm('¿Restaurar este template a los valores por defecto?')) return;
    
    try {
      await api.post(`/email/templates/${selectedTemplate.type}/reset`);
      toast.success('Template restaurado');
      await handleSelectTemplate(selectedTemplate);
      await fetchTemplates();
    } catch (err) {
      toast.error('Error restaurando template');
    }
  };
  
  const handlePreview = async () => {
    if (!selectedTemplate) return;
    try {
      const res = await api.post(`/email/templates/${selectedTemplate.type}/preview`);
      setPreviewContent(res.data);
      setShowPreview(true);
    } catch (err) {
      toast.error('Error generando preview');
    }
  };
  
  const handleToggleActive = async (template) => {
    try {
      await api.put(`/email/templates/${template.type}`, {
        is_active: !template.is_active
      });
      toast.success(template.is_active ? 'Template desactivado' : 'Template activado');
      await fetchTemplates();
      if (selectedTemplate?.type === template.type) {
        await handleSelectTemplate(template);
      }
    } catch (err) {
      toast.error('Error actualizando template');
    }
  };
  
  const handleInitDefaults = async () => {
    try {
      const res = await api.post('/email/templates/init-defaults');
      toast.success(`${res.data.templates_created} templates inicializados`);
      await fetchTemplates();
    } catch (err) {
      toast.error('Error inicializando templates');
    }
  };
  
  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/email/config', configForm);
      toast.success('Configuración guardada');
      await fetchConfig();
    } catch (err) {
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Introduce un email de destino');
      return;
    }
    setSendingTest(true);
    try {
      const res = await api.post(`/email/test?to=${encodeURIComponent(testEmail)}&template_type=${testTemplateType}`);
      if (res.data.success) {
        if (res.data.queued) {
          toast.info('Email en cola (configura Postmark para enviar)');
        } else {
          toast.success('Email de prueba enviado');
        }
      } else {
        toast.error(res.data.error || 'Error enviando email');
      }
    } catch (err) {
      toast.error('Error enviando email de prueba');
    } finally {
      setSendingTest(false);
    }
  };
  
  // Filter templates by category
  const filteredTemplates = categoryFilter === 'all' 
    ? templates 
    : templates.filter(t => t.category === categoryFilter);
  
  // ==================== RENDER ====================
  
  return (
    <div className="min-h-screen bg-app-bg flex">
      {/* Sidebar */}
      <aside className="w-64 bg-surface border-r border-border-subtle flex flex-col">
        <div className="p-4 border-b border-border-subtle">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand to-purple-600 flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-text-main">Email Marketing</h2>
              <p className="text-xs text-text-muted">Templates y logs</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-3 space-y-1">
          {/* Back to Dashboard button */}
          <button
            onClick={() => navigate('/admin/master')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-text-muted hover:text-text-main hover:bg-surface-hover mb-2 border border-border-subtle"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Volver al Panel</span>
          </button>
          
          {[
            { id: 'templates', label: 'Templates', icon: FileText },
            { id: 'config', label: 'Configuración', icon: Settings2 },
            { id: 'logs', label: 'Historial', icon: Clock },
          ].map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeTab === item.id 
                    ? 'bg-brand/10 text-brand' 
                    : 'text-text-muted hover:text-text-main hover:bg-surface-hover'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>
        
        {/* Stats Summary */}
        {stats && (
          <div className="p-3 border-t border-border-subtle">
            <div className="bg-app-bg rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-text-muted">Enviados</span>
                <span className="text-green-400 font-medium">{stats.sent}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-text-muted">Fallidos</span>
                <span className="text-red-400 font-medium">{stats.failed}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-text-muted">Hoy</span>
                <span className="text-text-main font-medium">{stats.today}</span>
              </div>
            </div>
          </div>
        )}
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 p-6 overflow-auto">
        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="flex gap-6 h-full">
            {/* Templates List */}
            <div className="w-80 flex-shrink-0 space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-text-main">Templates</h1>
                <Button variant="outline" size="sm" onClick={handleInitDefaults}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Inicializar
                </Button>
              </div>
              
              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setCategoryFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    categoryFilter === 'all' 
                      ? 'bg-brand/20 text-brand' 
                      : 'bg-surface text-text-muted hover:bg-surface-hover'
                  }`}
                >
                  Todos
                </button>
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                  <button
                    key={key}
                    onClick={() => setCategoryFilter(key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      categoryFilter === key 
                        ? cfg.color 
                        : 'bg-surface text-text-muted hover:bg-surface-hover'
                    }`}
                  >
                    {cfg.icon} {cfg.label}
                  </button>
                ))}
              </div>
              
              {/* Templates */}
              <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-auto pr-2">
                {filteredTemplates.map(template => {
                  const catConfig = CATEGORY_CONFIG[template.category];
                  return (
                    <div
                      key={template.type}
                      onClick={() => handleSelectTemplate(template)}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${
                        selectedTemplate?.type === template.type
                          ? 'border-brand bg-brand/5'
                          : 'border-border-subtle bg-surface hover:border-brand/30'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span>{catConfig?.icon}</span>
                          <span className="font-medium text-text-main text-sm">{template.name}</span>
                        </div>
                        {template.is_configured && (
                          <span className={`w-2 h-2 rounded-full ${template.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                        )}
                      </div>
                      <p className="text-xs text-text-muted mt-1 line-clamp-2">{template.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Template Editor/Preview */}
            <div className="flex-1 bg-surface border border-border-subtle rounded-xl overflow-hidden">
              {selectedTemplate && templateContent ? (
                <>
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between">
                    <div>
                      <h2 className="font-bold text-text-main text-sm">{selectedTemplate.name}</h2>
                      <p className="text-xs text-text-muted">{selectedTemplate.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 mr-2">
                        <span className="text-xs text-text-muted">Activo</span>
                        <Switch 
                          checked={templateContent.is_active}
                          onCheckedChange={() => handleToggleActive({...selectedTemplate, is_active: templateContent.is_active})}
                        />
                      </div>
                      <Button variant="outline" size="sm" onClick={handleResetTemplate} className="h-8">
                        <RotateCcw className="w-3 h-3 mr-1" /> Restaurar
                      </Button>
                      <Button variant="outline" size="sm" onClick={handlePreview} className="h-8">
                        <Eye className="w-3 h-3 mr-1" /> Preview
                      </Button>
                      <Button size="sm" onClick={handleOpenEditor} className="bg-brand h-8">
                        <Edit3 className="w-3 h-3 mr-1" /> Editar
                      </Button>
                    </div>
                  </div>
                  
                  {/* Variables */}
                  <div className="px-4 py-2 border-b border-border-subtle bg-app-bg/50">
                    <p className="text-xs text-text-muted mb-1">Variables disponibles:</p>
                    <div className="flex flex-wrap gap-1">
                      {templateContent.variables?.map(v => (
                        <code key={v} className="px-2 py-0.5 bg-surface rounded text-xs text-brand">
                          {`{{${v}}}`}
                        </code>
                      ))}
                    </div>
                  </div>
                  
                  {/* Preview Frame */}
                  <div className="p-3">
                    <div className="mb-2">
                      <p className="text-xs text-text-muted mb-1">Asunto:</p>
                      <p className="text-sm font-medium text-text-main bg-app-bg rounded-lg px-2 py-1.5 flex items-center gap-2">
                        {templateContent.subject}
                        <Edit3 className="w-3 h-3 text-text-muted" />
                      </p>
                    </div>
                    <div className="border border-border-subtle rounded-lg overflow-hidden bg-white" style={{ height: 'calc(100vh - 290px)', minHeight: '450px' }}>
                      <iframe
                        srcDoc={templateContent.html_content}
                        className="w-full h-full"
                        title="Email Preview"
                        sandbox=""
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-text-muted">
                  <div className="text-center">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Selecciona un template para ver o editar</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Config Tab */}
        {activeTab === 'config' && (
          <div className="max-w-2xl space-y-6">
            <h1 className="text-xl font-bold text-text-main">Configuración de Email</h1>
            
            {/* Postmark Config */}
            <div className="bg-surface border border-border-subtle rounded-xl p-6 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-main">Postmark</h3>
                  <p className="text-sm text-text-muted">Servicio de envío de emails transaccionales</p>
                </div>
                {config?.is_configured ? (
                  <span className="ml-auto px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                    Configurado
                  </span>
                ) : (
                  <span className="ml-auto px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                    Pendiente
                  </span>
                )}
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-text-muted mb-1">Server API Token</label>
                  <Input
                    type="password"
                    value={configForm.postmark_api_key}
                    onChange={(e) => setConfigForm(f => ({ ...f, postmark_api_key: e.target.value }))}
                    placeholder={config?.has_api_key ? '••••••••••••••••' : 'Introduce tu API Token de Postmark'}
                    className="bg-app-bg border-border-subtle"
                  />
                  <p className="text-xs text-text-muted mt-1">
                    Obtén tu token en <a href="https://account.postmarkapp.com/servers" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">account.postmarkapp.com</a>
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-text-muted mb-1">Email remitente</label>
                    <Input
                      value={configForm.sender_email}
                      onChange={(e) => setConfigForm(f => ({ ...f, sender_email: e.target.value }))}
                      placeholder="soporte@fascinai.pro"
                      className="bg-app-bg border-border-subtle"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-1">Nombre remitente</label>
                    <Input
                      value={configForm.sender_name}
                      onChange={(e) => setConfigForm(f => ({ ...f, sender_name: e.target.value }))}
                      placeholder="FASCINAI"
                      className="bg-app-bg border-border-subtle"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-border-subtle">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={configForm.is_enabled}
                      onCheckedChange={(checked) => setConfigForm(f => ({ ...f, is_enabled: checked }))}
                    />
                    <span className="text-sm text-text-main">Activar envío de emails</span>
                  </div>
                  <Button onClick={handleSaveConfig} disabled={saving} className="bg-brand">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Guardar
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Test Email */}
            <div className="bg-surface border border-border-subtle rounded-xl p-6">
              <h3 className="font-semibold text-text-main mb-4 flex items-center gap-2">
                <TestTube className="w-5 h-5" /> Enviar email de prueba
              </h3>
              <div className="flex gap-3">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="bg-app-bg border-border-subtle flex-1"
                />
                <select
                  value={testTemplateType}
                  onChange={(e) => setTestTemplateType(e.target.value)}
                  className="bg-app-bg border border-border-subtle rounded-lg px-3 text-sm text-text-main"
                >
                  {templates.filter(t => t.is_configured).map(t => (
                    <option key={t.type} value={t.type}>{t.name}</option>
                  ))}
                </select>
                <Button onClick={handleSendTestEmail} disabled={sendingTest}>
                  {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-text-main">Historial de Emails</h1>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-text-muted">{logsCounts.sent || 0} enviados</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-text-muted">{logsCounts.failed || 0} fallidos</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-text-muted">{logsCounts.queued || 0} en cola</span>
                </span>
              </div>
            </div>
            
            {/* Logs Table */}
            <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-app-bg/50">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-text-muted">Fecha</th>
                    <th className="text-left p-3 text-xs font-medium text-text-muted">Destinatario</th>
                    <th className="text-left p-3 text-xs font-medium text-text-muted">Template</th>
                    <th className="text-left p-3 text-xs font-medium text-text-muted">Asunto</th>
                    <th className="text-left p-3 text-xs font-medium text-text-muted">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-text-muted">
                        No hay emails en el historial
                      </td>
                    </tr>
                  ) : (
                    logs.map((log, idx) => (
                      <tr key={log.id || idx} className="border-t border-border-subtle hover:bg-surface-hover">
                        <td className="p-3 text-sm text-text-muted">{formatDate(log.created_at)}</td>
                        <td className="p-3">
                          <p className="text-sm text-text-main">{log.recipient}</p>
                          {log.recipient_name && (
                            <p className="text-xs text-text-muted">{log.recipient_name}</p>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-1 bg-brand/10 text-brand text-xs rounded-full">
                            {log.template_type}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-text-main max-w-xs truncate">{log.subject}</td>
                        <td className="p-3">
                          {log.status === 'sent' && (
                            <span className="flex items-center gap-1 text-green-400 text-sm">
                              <CheckCircle2 className="w-4 h-4" /> Enviado
                            </span>
                          )}
                          {log.status === 'failed' && (
                            <span className="flex items-center gap-1 text-red-400 text-sm" title={log.error}>
                              <XCircle className="w-4 h-4" /> Fallido
                            </span>
                          )}
                          {log.status === 'queued' && (
                            <span className="flex items-center gap-1 text-amber-400 text-sm">
                              <Clock className="w-4 h-4" /> En cola
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
      
      {/* Editor Modal */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="bg-surface border-border-subtle max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-text-main flex items-center gap-2">
              <Edit3 className="w-5 h-5" />
              Editar Template: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Subject */}
            <div className="p-4 border-b border-border-subtle">
              <label className="block text-sm text-text-muted mb-1">Asunto del email</label>
              <Input
                value={editorSubject}
                onChange={(e) => setEditorSubject(e.target.value)}
                placeholder="Asunto..."
                className="bg-app-bg border-border-subtle"
              />
              <p className="text-xs text-text-muted mt-1">
                Usa variables como {`{{user_name}}`} para personalizar
              </p>
            </div>
            
            {/* Editor Tabs */}
            <Tabs value={editMode} onValueChange={setEditMode} className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 border-b border-border-subtle">
                <TabsList className="bg-transparent">
                  <TabsTrigger value="visual" className="data-[state=active]:bg-brand/10">
                    <Palette className="w-4 h-4 mr-1" /> Visual
                  </TabsTrigger>
                  <TabsTrigger value="code" className="data-[state=active]:bg-brand/10">
                    <Code className="w-4 h-4 mr-1" /> HTML
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="visual" className="flex-1 overflow-auto p-4 m-0">
                <div className="grid grid-cols-2 gap-4 h-full">
                  {/* WYSIWYG Editor - Simplified */}
                  <div className="border border-border-subtle rounded-lg overflow-hidden">
                    <div className="bg-app-bg px-3 py-2 border-b border-border-subtle text-xs text-text-muted">
                      Editor HTML
                    </div>
                    <textarea
                      value={editorHtml}
                      onChange={(e) => setEditorHtml(e.target.value)}
                      className="w-full h-[400px] p-3 bg-surface text-text-main text-sm font-mono resize-none focus:outline-none"
                      placeholder="HTML del email..."
                    />
                  </div>
                  
                  {/* Live Preview */}
                  <div className="border border-border-subtle rounded-lg overflow-hidden">
                    <div className="bg-app-bg px-3 py-2 border-b border-border-subtle text-xs text-text-muted">
                      Vista previa
                    </div>
                    <div className="bg-white h-[400px] overflow-auto">
                      <iframe
                        srcDoc={editorHtml}
                        className="w-full h-full"
                        title="Preview"
                        sandbox=""
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="code" className="flex-1 overflow-auto p-4 m-0">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-text-muted mb-1">HTML</label>
                    <textarea
                      value={editorHtml}
                      onChange={(e) => setEditorHtml(e.target.value)}
                      className="w-full h-64 p-3 bg-app-bg border border-border-subtle rounded-lg text-text-main text-sm font-mono resize-none focus:outline-none focus:border-brand"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-1">Texto plano (fallback)</label>
                    <textarea
                      value={editorText}
                      onChange={(e) => setEditorText(e.target.value)}
                      className="w-full h-32 p-3 bg-app-bg border border-border-subtle rounded-lg text-text-main text-sm font-mono resize-none focus:outline-none focus:border-brand"
                      placeholder="Versión de texto plano para clientes de email que no soportan HTML..."
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            
            {/* Footer */}
            <div className="p-4 border-t border-border-subtle flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowEditor(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveTemplate} disabled={saving} className="bg-brand">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Guardar template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-surface border-border-subtle max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-text-main">Vista previa del email</DialogTitle>
          </DialogHeader>
          
          {previewContent && (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-text-muted mb-1">Asunto:</p>
                <p className="text-sm font-medium text-text-main bg-app-bg rounded-lg p-3">
                  {previewContent.subject}
                </p>
              </div>
              
              <div>
                <p className="text-xs text-text-muted mb-1">Variables utilizadas:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(previewContent.variables_used || {}).map(([key, value]) => (
                    <span key={key} className="px-2 py-1 bg-app-bg rounded text-xs">
                      <span className="text-brand">{key}</span>: <span className="text-text-main">{value}</span>
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="border border-border-subtle rounded-lg overflow-hidden">
                <iframe
                  srcDoc={previewContent.html_content}
                  className="w-full h-[500px] bg-white"
                  title="Email Preview"
                  sandbox=""
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailTemplates;
